import {ReactElement, ReactNode} from "react";
import styled from "styled-components";

import IconAttente from "@/assets/iconAttente.svg?react";
import IconRefuse from "@/assets/iconCross.svg?react";
import IdconValide from "@/assets/iconValide.svg?react";
import {ErrorMessage} from "@/components/ErrorMessage/ErrorMessage";
import {Tooltips} from "@/components/Tooltips/Tooltips";
import {calculePourcentage} from "@/fonction/calcule";
import {upperCaseFirsteLetter} from "@/fonction/uppercaseFirstLetter";
import {breakpoints, quicksandMedium, quicksandSemiBold} from "@/GlobalStyle";
import {KpiEnCours as KpiEnCoursType} from "@/ts/kpi";

const KpiAddedList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 0.625rem;
  margin-top: -3px;
  margin: 10px 0;
  container: kpiEnCours / inline-size;

  & button {
    align-self: center;
    width: 135px;
    justify-content: center;
  }
  @media (max-width: ${breakpoints.lg}) {
    & button {
      width: auto;
      & span {
        display: none;
      }
    }
    flex-direction: column;
  }
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
  @media (max-width: ${breakpoints.sm}) {
    & button {
      padding: 7px 10px;
    }
  }
`;

const KpiAddedListContainer = styled.div`
  container: kpiContainer / inline-size;
  width: 100%;
  max-width: calc(100% - 190px);

  @media (max-width: ${breakpoints.lg}) {
    width: 100%;
    max-width: 100%;
  }
`;
const KpiAddedListContent = styled.div`
  column-gap: 0.625rem;
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  min-height: 100%;

  & h3 {
    margin: 0;
    ${quicksandSemiBold}
    font-size: 1.25rem;
    color: ${({theme}) => theme.colors.mediumPrimary};
    display: flex;
    gap: 10px;
  }
  @media (max-width: ${breakpoints.md}) {
    width: 100%;
    margin: 1.25rem 0 0 0;
    flex-direction: column;
    padding: 0;
    border: 0;
    & h3 {
      font-size: 1rem;
    }
  }
  @container kpiContainer (width <= 820px) {
    flex-direction: column;
  }
`;
const KpiAddedInfoComplementaire = styled.p`
  color: ${({theme}) => theme.colors.mediumPrimary};
  font-size: 1rem;
  ${quicksandMedium}
`;
const KpiAddedElementContentLeft = styled.div`
  display: flex;
  align-items: center;
  min-width: 400px;
  flex: 1;

  border: 2px solid ${({theme}) => theme.colors.darkPrimary};
  @media (max-width: ${breakpoints.md}) {
    padding: 10px 5px;
  }
  @container kpiContainer (width <= 820px) {
    padding: 0 10px;
    border-bottom: none;
    min-width: 0;
  }
`;

const KpiAddedName = styled.div`
  padding: 15px 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  & > p {
    margin-left: 20px;
  }
  @media (max-width: ${breakpoints.md}) {
    padding: 10px 5px;
  }
`;
const KpiElementRight = styled.div`
  display: flex;
  gap: 0.625rem;
  min-width: 350px;
  justify-content: space-around;
  @container kpiContainer (width <= 820px) {
    gap: 0;
    flex-wrap: wrap;
    min-width: 0;
  }
`;

const KpiStatValeurBloc = styled.div<{$borderRight: boolean}>`
  height: calc(100% - 20px);
  width: auto;
  min-width: 100px;
  padding: 10px;

  display: flex;
  flex-direction: column;

  align-items: center;

  & h6,
  & p {
    margin: 0;
    text-align: center;

    font-size: 1rem;
    ${quicksandSemiBold}
  }
  & p {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    & span {
      margin-left: 5px;
      font-size: 0.875rem;
    }
  }
  &#Valeur {
    background-color: ${({theme}) => theme.colors.darkPrimary};
    & h6,
    & p {
      color: white;
    }
    & p {
      margin: 3px 0;
    }
  }
  &#Objectif {
    background-color: ${({theme}) => theme.colors.lightPrimary};
    & h6,
    & p {
      color: white;
    }
  }
  &#Status_en-attente {
    background-color: ${({theme}) => theme.colors.darkSecondary};
    & h6,
    & p {
      color: white;
    }
    & svg {
      fill: white;
      max-width: 2rem;
    }
  }
  &#Status_accepte {
    background-color: ${({theme}) => theme.colors.shadow};
    & h6,
    & p {
      color: white;
    }

    & svg {
      fill: white;
      max-width: 2rem;
    }
    &.objectif-validated {
      background-color: ${({theme}) => theme.colors.validate};
    }
  }
  &#Status_refuse {
    background-color: ${({theme}) => theme.colors.alert};
    & h6,
    & p {
      color: white;
    }
    & svg {
      fill: white;
      max-width: 2rem;
    }
  }
  &#No_Status {
    background-color: ${({theme}) => theme.colors.shadow};
  }
  @container kpiContainer (width <= 820px) {
    border: none;
    height: auto;
    flex: 1;
    min-width: 120px;
    padding: 10px 5px;
  }
`;
const ContentSvg = styled.div`
  width: 100%;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const ContainerChildren = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 180px;
`;

const Arrow = styled.div`
  position: relative;
  height: 24px;
  width: 15px;
  max-width: 15px;
  flex: none;
  transform: rotate(90deg);
  margin: 5px;
`;
const PointArrow = styled.div`
  width: 5px;
  height: 5px;
  border: solid white;
  border-width: 0 1.5px 1.5px 0;
  display: inline-block;
  transform: rotate(-45deg);
  padding: 3px;
  flex: none;
  position: absolute;
  right: 1px;
  top: 6px;
`;

const TigeArrow = styled.div`
  width: 15px;
  height: 1.5px;
  background-color: white;
  display: inline-block;
  position: absolute;
  top: 11px;
`;

export function KpiEnCours({
  kpi,
  children,
  errorMessage,
}: {
  kpi: KpiEnCoursType;
  children: ReactNode;
  errorMessage?: string;
}): ReactElement {
  const status = kpi.evenements?.[0]?.statut.replaceAll(" ", "_").toLowerCase();
  const statusIcon = (): ReactElement | null => {
    switch (status) {
      case "en-attente":
        return <IconAttente />;
      case "accepte":
        return <IdconValide />;
      case "refuse":
        return <IconRefuse />;
      default:
        return null;
    }
  };
  const currentEvenements = kpi?.evenements?.filter(
    evenement =>
      upperCaseFirsteLetter(evenement.statut) !== "refuse" &&
      upperCaseFirsteLetter(evenement.statut) !== "accepter"
  );
  const currentPourcentageRealise = calculePourcentage(
    kpi?.valeur,
    kpi?.objectif
  );

  return (
    <KpiAddedList data-testid="KpiAddedList">
      <KpiAddedListContainer data-testid="KpiAddedListContainer">
        <KpiAddedListContent data-testid="KpiAddedListContent">
          <KpiAddedElementContentLeft data-testid="KpiAddedElementContentLeft">
            <KpiAddedName data-testid="KpiAddedName">
              <h3>
                {kpi?.nom}
                {kpi?.description ? (
                  <Tooltips content={kpi?.description} data-testid="Tooltips" />
                ) : null}
              </h3>
              <KpiAddedInfoComplementaire data-testid="KpiAddedInfoComplementaire">
                {kpi?.informations ? `- ${kpi?.informations}` : ""}
              </KpiAddedInfoComplementaire>
              {errorMessage ? <ErrorMessage text={errorMessage} /> : null}
            </KpiAddedName>
          </KpiAddedElementContentLeft>
          <KpiElementRight>
            <KpiStatValeurBloc
              data-testid="KpiStatValeurBloc"
              id="Valeur"
              $borderRight={true}>
              <h6>Réalisé</h6>
              <p>
                {kpi?.valeur}
                <span>{` (${currentPourcentageRealise}%)`}</span>
              </p>
              {currentEvenements?.length &&
              currentEvenements?.[0]?.statut === "en-attente" ? (
                <>
                  <Arrow>
                    <TigeArrow /> <PointArrow></PointArrow>
                  </Arrow>
                  <p>
                    {kpi?.valeur + currentEvenements?.[0]?.increment}
                    <span>{` (${calculePourcentage(kpi?.valeur + currentEvenements?.[0]?.increment, kpi.objectif)}%)`}</span>
                  </p>
                </>
              ) : null}
            </KpiStatValeurBloc>
            <KpiStatValeurBloc
              data-testid="KpiStatValeurBloc"
              id="Objectif"
              $borderRight={true}>
              <h6>Objectif</h6>
              <p>{kpi?.objectif}</p>
            </KpiStatValeurBloc>

            <KpiStatValeurBloc
              id={status ? `Status_${status}` : "No_Status"}
              className={
                currentPourcentageRealise >= 100 ? "objectif-validated" : ""
              }
              $borderRight={false}>
              {kpi?.evenements?.[0]?.statut ? (
                <>
                  <h6 data-testid="status-evenement-kpi">
                    {currentPourcentageRealise >= 100
                      ? "KPI validé"
                      : status === "accepte"
                        ? "KPI en cours"
                        : upperCaseFirsteLetter(
                            kpi?.evenements?.[0]?.statut
                          ).replaceAll("-", " ")}
                  </h6>
                  <ContentSvg> {statusIcon()}</ContentSvg>
                </>
              ) : null}
            </KpiStatValeurBloc>
          </KpiElementRight>
        </KpiAddedListContent>
      </KpiAddedListContainer>
      {children ? <ContainerChildren>{children}</ContainerChildren> : null}
    </KpiAddedList>
  );
}
